import { useCallback, useEffect, useState } from "react";
import { getRouteApi } from "@tanstack/react-router";

import { PartType } from "components/homepage/PartCard";
import Modal from "components/modals/Modal";
import CreateTeamPage from "components/onboarding/CreateTeamPage";
import ExperienceSelection from "components/onboarding/ExperienceSelection";
import GeneratedFeedPreviewInputs from "components/onboarding/GeneratedFeedPreviewInputs";
import JoinTeamPage from "components/onboarding/JoinTeamPage";
import ProfileSetup from "components/onboarding/ProfileSetup";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { TeamsQuery } from "lib/generated/app-service-gql/graphql";
import { useUpdateCompany } from "lib/hooks/api/company/useUpdateCompany";
import { useKeywords } from "lib/hooks/api/keywords/useKeywords";
import { useSubmitOnboarding } from "lib/hooks/api/onboarding/useSubmitOnboarding";
import { useTeams } from "lib/hooks/api/teams/useTeams";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { routes } from "lib/routes";
import { SelfServeQuery } from "lib/StotlesApi";
import * as tracking from "lib/tracking";
import { Status } from "lib/types/models";
import { ACCOUNT_SETUP_FORM } from "../../components/onboarding/tracking";
import { sendGa4Event } from "../../lib/ga4Tracking";

import css from "./OnboardingPage.module.scss";

import FRESHWORKS_LOGO from "../../../assets/images/onboarding/freshworks_logo.png";
import KEYWORD_EXAMPLE from "../../../assets/images/onboarding/keywords_example.png";
import TEAMS_EXAMPLE from "../../../assets/images/onboarding/teams_example.png";

const routeApi = getRouteApi("/onboarding");

export enum Steps {
  PROFILE_SETUP,
  EXPERIENCE,
  JOIN_TEAM,
  CREATE_TEAM,
  KEYWORDS,
}

export const OTHER_INDUSTRY = "Other";
export type OTHER_INDUSTRY_TYPE = "Other";

export type FormState = {
  experience?: string;
  companySize?: string;
  countries: string[];
  languages?: string[];
  keywords: string[];
  mainGoal?: PartType;
};

const initialFormState: FormState = {
  experience: undefined,
  companySize: undefined,
  countries: ["United Kingdom", "Ireland"],
  keywords: [],
};

const defaultFrom = new Date();
defaultFrom.setMonth(defaultFrom.getMonth() - 1);
defaultFrom.setHours(0, 0, 0, 0);

export function formStateToFeedCriteria(
  formState: FormState,
  statusFilters: Status[],
): SelfServeQuery {
  return {
    countries: formState.countries,
    languages: formState.languages,
    keywords: {
      keywords: formState.keywords,
      from_date: defaultFrom,
      status: statusFilters,
    },
  };
}

function filterTeams(teams: TeamsQuery["teams"], currentTeamId: string) {
  return teams.filter((team) => {
    if (team.id === currentTeamId) {
      return false;
    }
    if (team.users.length === 0) {
      return false;
    }
    if (!team.feedSettingsId) {
      return false;
    }
    return true;
  });
}

export function OnboardingPage() {
  const { redirect } = routeApi.useSearch();
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.PROFILE_SETUP);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [shouldRefreshFeed, setShouldRefreshFeed] = useState<boolean>(false);
  const shouldSkipKeywordSelection = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const { mutate: updateCompany } = useUpdateCompany();

  const currentUser = useAssertCurrentUser();

  const onOnboardingComplete = useCallback(() => {
    const getRedirectUrl = (
      seoParams: { dataType?: string | null; id?: string | null } | null | undefined,
    ) => {
      if (redirect) return redirect;

      if (seoParams) {
        const { dataType, id } = seoParams;

        if (dataType === "buyer" && id) {
          return `${routes.buyer}/${id}`;
        }

        if (dataType === "record" && id) {
          return `${routes.notices}/${id}`;
        }
      }

      return routes.home;
    };

    // Full page load (not a SPA navigate) on purpose: onboarding has just created the
    // company's signal settings, so we need a fresh current user fetched at the destination.
    // It also supports `redirect` paths that may not be in the SPA route tree.
    window.location.href = getRedirectUrl(currentUser.seoParams);
  }, [currentUser, redirect]);

  const { data, isLoading } = useKeywords({ enabled: !shouldSkipKeywordSelection });

  const { data: teams } = useTeams();

  const filteredTeams = teams ? filterTeams(teams, currentUser.team.id) : [];

  useEffect(() => {
    setFormState((prevFormState) => {
      return {
        ...prevFormState,
        keywords: data?.keywords || [],
      };
    });
  }, [data?.keywords]);

  const { mutate, isLoading: isSubmittingOnboarding } = useSubmitOnboarding({
    onSuccess: () => onOnboardingComplete(),
  });

  const handleSubmit = (
    params: { keywords?: string[] },
    shouldRefreshFeed: boolean,
    allowEmptyKeywords?: boolean,
  ) => {
    mutate({
      formState: { ...formState, keywords: params.keywords ?? formState.keywords },
      shouldRefreshFeed,
      allowEmptyKeywords,
    });

    sendGa4Event({
      event: "account_setup_form_submit",
      form: ACCOUNT_SETUP_FORM,
    });
  };

  // Amplitude page tracking per step in the process
  useEffect(() => {
    switch (currentStep) {
      case Steps.PROFILE_SETUP:
        tracking.pageView("Onboarding - Profile Setup", {});
        sendGa4Event({
          event: "continue_click",
          form: ACCOUNT_SETUP_FORM,
        });
        break;
      case Steps.EXPERIENCE:
        tracking.pageView("Onboarding - Select Public Sector Experience and Employee Count", {});
        sendGa4Event({
          event: "continue_click",
          form: ACCOUNT_SETUP_FORM,
        });
        break;
      case Steps.JOIN_TEAM:
        tracking.pageView("Onboarding - Team - Join Team", {});
        sendGa4Event({
          event: "continue_click",
          form: ACCOUNT_SETUP_FORM,
        });
        break;
      case Steps.CREATE_TEAM:
        tracking.pageView("Onboarding - Team - New Team", {});
        sendGa4Event({
          event: "continue_click",
          form: ACCOUNT_SETUP_FORM,
        });
        break;
      case Steps.KEYWORDS:
        tracking.pageView("Onboarding - Select Keywords", {});
        sendGa4Event({
          event: "continue_click",
          form: ACCOUNT_SETUP_FORM,
        });
        break;
    }
  }, [currentStep]);

  const renderLeftPane = () => {
    switch (currentStep) {
      case Steps.PROFILE_SETUP:
        return (
          <ProfileSetup
            onComplete={() => setCurrentStep(Steps.EXPERIENCE)}
            companyNameEditable={!currentUser.company.completedOnboarding}
          />
        );
      case Steps.EXPERIENCE:
        return (
          <ExperienceSelection
            onSubmit={(value) => {
              setFormState({
                ...formState,
                experience: value.experience,
              });
              updateCompany({
                input: {
                  guid: currentUser.company.guid,
                  employeeCount: value.employeeCount,
                },
              });

              if (filteredTeams.length > 0) {
                setCurrentStep(Steps.JOIN_TEAM);
              } else {
                setCurrentStep(Steps.CREATE_TEAM);
              }
            }}
          />
        );
      case Steps.JOIN_TEAM:
        return (
          <JoinTeamPage
            teams={filteredTeams}
            companyName={currentUser.company.name}
            onCreateTeam={() => {
              setCurrentStep(Steps.CREATE_TEAM);
            }}
            onJoinTeam={() => {
              setShouldRefreshFeed(false);
              handleSubmit({}, false);
            }}
          />
        );
      case Steps.CREATE_TEAM:
        return (
          <CreateTeamPage
            onSubmit={() =>
              shouldSkipKeywordSelection
                ? handleSubmit({}, false, shouldSkipKeywordSelection)
                : setCurrentStep(Steps.KEYWORDS)
            }
            companyName={currentUser.company.name}
            onBack={filteredTeams.length > 0 ? () => setCurrentStep(Steps.JOIN_TEAM) : undefined}
            isSubmittingOnboarding={shouldSkipKeywordSelection && isSubmittingOnboarding}
          />
        );
      case Steps.KEYWORDS:
        return (
          <GeneratedFeedPreviewInputs
            keywords={formState.keywords}
            countries={formState.countries}
            isLoading={isLoading}
            onSubmit={(keywords) => {
              const newFormState = { ...formState, keywords };
              setFormState(newFormState);
              setShouldRefreshFeed(true);
              handleSubmit({ keywords: newFormState.keywords }, true);
            }}
          />
        );
    }
  };

  const renderRightPane = () => {
    switch (currentStep) {
      case Steps.PROFILE_SETUP:
      case Steps.EXPERIENCE:
        return (
          <div className={`${css.rightImageContainer} ${css.authorisation}`}>
            <div className={css.content}>
              <img src={FRESHWORKS_LOGO} alt="Freshworks Logo" className={css.companyLogo} />
              <p className={css.quote}>
                "We're more aware of everything that's going on in the market and can use the
                information to make sure we engage the right people"
              </p>
              <p className={css.quoteAuthor}>Olly Phillips</p>
              <p className={css.quoteAuthorTitle}>Director of Sales, Freshworks</p>
            </div>
          </div>
        );
      case Steps.JOIN_TEAM:
      case Steps.CREATE_TEAM:
        return (
          <div className={`${css.rightImageContainer} ${css.teams}`}>
            <img src={TEAMS_EXAMPLE} alt="Teams example" className={css.rightImage} />
          </div>
        );
      case Steps.KEYWORDS:
        return (
          <div className={`${css.rightImageContainer} ${css.keywords}`}>
            <img src={KEYWORD_EXAMPLE} alt="Keywords example" className={css.rightImage} />
          </div>
        );
    }
  };

  return (
    <div className={css.onboardingContainer}>
      <div className={css.onboarding}>
        <Modal isOpen={isSubmittingOnboarding && shouldRefreshFeed} centered>
          <h1 className={css.loadingHeader}>Setting up your feed 🎉</h1>
          <p className={css.loadingBody}>This should only take a moment</p>
        </Modal>
        <div className={css.leftPane}>
          <div className={css.leftPaneInner}>{renderLeftPane()}</div>
        </div>
        <div className={css.rightPane}>{renderRightPane()}</div>
      </div>
    </div>
  );
}

export default OnboardingPage;
